import { Header } from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Shield, FileText, Lock, AlertCircle, Upload } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";

const LegalInformation = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="container mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-8"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        <div className="prose max-w-none">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="h-8 w-8 text-primary" />
            <h1 className="text-4xl font-bold m-0">Legal Information</h1>
          </div>
          <p className="text-gray-600">Last updated: {new Date().toLocaleDateString()}</p>
          
          <div className="mt-8 space-y-8">
            <section className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="h-6 w-6 text-primary" />
                <h2 className="text-2xl font-semibold m-0">Copyright Infringement Policy</h2>
              </div>
              <p className="text-gray-700">Bright Stages, LLC ("Bright Stages") respects the intellectual property rights of others and is committed to complying with all applicable copyright laws. We expect our users to uphold the same standards. If you believe that any content on our platform—including but not limited to text, images, data, or other materials—infringes upon your copyrighted work, you may submit a formal Copyright Infringement Notice following the procedure outlined below.</p>

              <h3 className="text-xl font-semibold mt-6 mb-4">Filing a Copyright Infringement Notice</h3>
              <p className="text-gray-700">To ensure prompt action, all copyright infringement claims must be submitted in writing and include the following details:</p>
              
              <div className="space-y-6 mt-4">
                <div>
                  <h4 className="font-semibold text-lg text-gray-900">Your Contact Information</h4>
                  <ul className="list-disc pl-6 space-y-1 text-gray-700">
                    <li>Full legal name</li>
                    <li>Mailing address</li>
                    <li>Telephone number</li>
                    <li>Valid email address</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-lg text-gray-900">Description of the Copyrighted Work</h4>
                  <ul className="list-disc pl-6 space-y-1 text-gray-700">
                    <li>Identify the copyrighted work that has allegedly been infringed.</li>
                    <li>If multiple works are affected, provide a representative list.</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-lg text-gray-900">Identification of the Infringing Material</h4>
                  <ul className="list-disc pl-6 space-y-1 text-gray-700">
                    <li>Clearly specify the exact location of the allegedly infringing material on our platform.</li>
                    <li>Include URLs, file names, or other descriptive details that allow us to locate the content.</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-lg text-gray-900">Good Faith Statement</h4>
                  <p className="text-gray-700 mt-2">Include the following statement:</p>
                  <p className="text-gray-700 mt-2 italic">"I have a good faith belief that the use of the copyrighted material described above is not authorized by the copyright owner, its agent, or applicable law."</p>
                </div>

                <div>
                  <h4 className="font-semibold text-lg text-gray-900">Accuracy and Authorization Declaration</h4>
                  <p className="text-gray-700 mt-2">Include the following statement under penalty of perjury:</p>
                  <p className="text-gray-700 mt-2 italic">"I declare under penalty of perjury that the information in this notice is accurate and that I am the copyright owner or authorized to act on behalf of the owner of an exclusive right that is allegedly infringed."</p>
                </div>

                <div>
                  <h4 className="font-semibold text-lg text-gray-900">Signature</h4>
                  <p className="text-gray-700 mt-2">A physical or electronic signature of the copyright owner or an authorized representative.</p>
                </div>
              </div>

              <div className="mt-6 bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold mb-2">All copyright infringement claims should be directed to our Designated Copyright Agent:</p>
                <p className="text-gray-700">
                  Bright Stages, LLC<br />
                  10624 S Eastern Ave., Ste A #724, Henderson, NV 89052<br />
                  Email: info@BrightStages.com
                </p>
              </div>

              <div className="mt-6 space-y-4">
                <h4 className="font-semibold text-lg text-gray-900">Investigation & Response</h4>
                <p className="text-gray-700">Upon receiving a valid Copyright Infringement Notice, Bright Stages, LLC will:</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Review the claim to determine its validity.</li>
                  <li>Take appropriate action, which may include removing or disabling access to the allegedly infringing content.</li>
                  <li>Notify the affected user (if applicable) and provide them with an opportunity to respond under the DMCA counter-notice procedure, if applicable.</li>
                </ul>
                <p className="text-gray-700 mt-4">Bright Stages, LLC reserves the right to reject incomplete or improperly formatted claims. Submitting a false infringement notice may result in legal consequences, including liability for damages and attorneys' fees.</p>
              </div>

              <p className="mt-4 text-gray-700">Upon receiving a valid notice, Bright Stages, LLC will investigate and take appropriate action, which may include removing or disabling access to the allegedly infringing content.</p>
            </section>

            <section className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <AlertCircle className="h-6 w-6 text-primary" />
                <h3 className="text-2xl font-semibold m-0">Counter-Notification Process</h3>
              </div>
              <p className="text-gray-700">If you believe that your content was wrongfully removed due to a copyright claim, you may submit a Counter-Notification containing the following details:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Your name, address, telephone number, and email address.</li>
                <li>Identification of the content that was removed or disabled and its previous location.</li>
                <li>A statement under penalty of perjury that you have a good faith belief that the content was removed due to a mistake or misidentification.</li>
                <li>A statement consenting to the jurisdiction of the federal court in your district and agreeing to accept service of process from the person who submitted the original infringement claim.</li>
                <li>Your physical or electronic signature.</li>
              </ul>
              <p className="mt-4 text-gray-700">Counter-notifications should be sent to the Designated Copyright Agent listed above. Upon receipt, Bright Stages, LLC may reinstate the removed content if appropriate under applicable law.</p>
            </section>

            <section className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Lock className="h-6 w-6 text-primary" />
                <h2 className="text-2xl font-semibold m-0">Additional Legal Information</h2>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold mb-2">1. Terms of Service</h3>
                  <p className="text-gray-700">By accessing or using Bright Stages, LLC's ("Bright Stages") platform, you acknowledge and agree to comply with our <Button 
                    variant="link" 
                    onClick={() => navigate("/terms")} 
                    className="text-primary p-0 h-auto font-normal hover:no-underline"
                  >Terms of Service</Button>, which govern permitted activities, prohibited conduct, disclaimers of liability, and other legal provisions. Violations may result in the suspension or termination of access to our services.</p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2">2. Privacy Policy</h3>
                  <p className="text-gray-700">Bright Stages is committed to protecting user privacy. Our <Button 
                    variant="link" 
                    onClick={() => navigate("/privacy")} 
                    className="text-primary p-0 h-auto font-normal hover:no-underline"
                  >Privacy Policy</Button> details how we collect, use, store, and protect personal information while ensuring compliance with applicable privacy laws. By using our platform, you consent to the data practices described in our Privacy Policy.</p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2">3. Limitation of Liability</h3>
                  <p className="text-gray-700">Bright Stages, LLC and its affiliates, officers, employees, or licensors shall not be liable for any direct, indirect, incidental, consequential, or punitive damages arising from or related to the use of our platform, including but not limited to:</p>
                  <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-700">
                    <li>Data loss or corruption</li>
                    <li>Technical errors, service interruptions, or security breaches</li>
                    <li>User reliance on assessment results or milestone tracking</li>
                  </ul>
                  <p className="text-gray-700 mt-4">Our developmental assessments are intended for educational and informational purposes only and do not constitute professional medical, psychological, or developmental advice. Users are encouraged to consult qualified professionals for any medical or developmental concerns.</p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2">4. User Content & Permissions</h3>
                  <p className="text-gray-700">Users who submit, upload, or share content on the Bright Stages platform grant Bright Stages, LLC a limited, non-exclusive, worldwide, royalty-free license to use, display, and analyze such content for research, educational, and analytical purposes.</p>
                  <p className="text-gray-700 mt-4">By submitting content, users affirm that they own or have the necessary rights to provide the material and that their submission does not infringe upon third-party rights. Bright Stages reserves the right to remove any content that violates these terms or applicable laws.</p>
                </div>
              </div>
            </section>
          </div>

          <p className="mt-8 text-gray-600">For legal inquiries or further clarification, please contact us at info@BrightStages.com.</p>
        </div>

        {/* Footer Links */}
        <footer className="py-8 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row justify-center items-center space-y-2 md:space-y-0 text-sm md:text-base md:space-x-8">
              <div className="flex space-x-4 md:space-x-8">
                <Button 
                  variant="link" 
                  onClick={() => navigate("/privacy")} 
                  className="text-[#1A1F2C] font-semibold hover:text-[#005b96] transition-colors whitespace-nowrap"
                >
                  Privacy Policy
                </Button>
                <Button 
                  variant="link" 
                  onClick={() => navigate("/legal-information")} 
                  className="text-[#1A1F2C] font-semibold hover:text-[#005b96] transition-colors whitespace-nowrap"
                >
                  Legal Information
                </Button>
              </div>
              <Button 
                variant="link" 
                onClick={() => navigate("/terms")} 
                className="text-[#1A1F2C] font-semibold hover:text-[#005b96] transition-colors whitespace-nowrap"
              >
                Terms & Acknowledgments
              </Button>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default LegalInformation;
